<template>
  <!-- Container for the modal background -->
  <!-- Sets a name for the modal background element -->

  <div v-if="isModalActive">
    <div
      v-show="!isMinimized"
      name="modalBackground"
      :class="[$style.modalBackground]"
      @click.self="handleAttemptClose"
    >
      <div
        :id="modalId"
        ref="modalCard"
        :class="['modal-card', $style.displayModal]"
        :style="modalStyle"
      >
        <header
          :class="['modal-card-head', $style.modalCardHead]"
          @mousedown.prevent="handleMouseDown"
          :style="{ cursor: draggable ? 'move' : 'default' }"
        >
          <!-- Header -->
          <p :class="$style.modalCardTitle">
            {{ modalTitle }}
          </p>

          <!-- Minimize Button -->
          <button
            type="button"
            :class="$style.headerButton"
            @click="$emit('minimize')"
          >
            <i class="mdi mdi-window-minimize"></i>
          </button>

          <!-- Close Button -->
          <button
            type="button"
            :class="$style.headerButton"
            @click="handleAttemptClose()"
          >
            <i class="mdi mdi-close"></i>
          </button>
        </header>
        <section class="modal-card-body">
          <template>
            <slot name="modal-body"></slot>
          </template>
        </section>
        <footer :class="['modal-card-foot', $style.modalCardFoot]">
          <template>
            <slot name="modal-footer"></slot>
          </template>
        </footer>
      </div>
      <!-- </b-modal> -->
    </div>
    <!-- When the card hasn't been opened at all in the first place, then the modal is not active. It's active when it has been opened at least once. -->
    <div v-show="isMinimized">
      <MinimisedCard
        ref="minimisedCardRef"
        :modalTitle="modalTitle"
        :modalContent="internalModalContent"
        @restore="$emit('restore')"
        @close="handleAttemptClose(true)"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import MinimisedCard from './MinimisedCard.vue';
import { namespace } from 'vuex-class';
import { showUnsavedChangesDialog } from '@/utils/dialog.util';

const unsavedChangesGuard = namespace('unsavedChangesGuard');

@Component({
  components: { MinimisedCard },
})
export default class InteractiveModal extends Vue {
  @Prop() public modalId!: string;
  @Prop() public modalTitle!: string;
  @Prop({ type: String, default: '' }) public modalContent!: string;
  @Prop({ default: false }) public isModalActive!: boolean;
  @Prop({ default: false }) public isMinimized!: boolean;
  @Prop({ default: true }) public draggable!: boolean;
  @Prop({ type: Boolean, default: false }) public isDirty!: boolean;

  // Current position of the modal card
  public modalPosX: number = 0;
  public modalPosY: number = 0;

  // State for mouse-based dragging
  private isDragging: boolean = false;
  private startMouseX: number = 0;
  private startMouseY: number = 0;
  private startModalX: number = 0;
  private startModalY: number = 0;

  // Flag to track whether initial position has been set
  private initialPositionSet: boolean = false;

  @unsavedChangesGuard.Action('notifyModalOpened')
  public notifyModalOpened!: () => Promise<void>;

  @unsavedChangesGuard.Action('notifyModalClosed')
  public notifyModalClosed!: () => Promise<void>;

  @unsavedChangesGuard.Action('notifyKeepEditingSelected')
  public notifyKeepEditingSelected!: () => Promise<void>;

  /**
   * Computed property that safely transforms modalContent for child components.
   * Ensures we always pass a string, never null.
   */
  get internalModalContent(): string {
    return this.modalContent || '';
  }

  // Generates inline style string for positioning the modal card
  get modalStyle(): string {
    return `left: ${this.modalPosX}px; top: ${this.modalPosY}px;`;
  }

  /**
   * Watcher for the isModalActive prop.
   * When the modal becomes active and the initial position hasn't been set yet, calculate and set the centered position
   * @param isActive - The new value of isModalActive
   */
  @Watch('isModalActive')
  public onIsModalActiveChange(isActive: boolean): void {
    if (isActive) {
      // Notify Vuex that a modal is now active.
      this.notifyModalOpened();

      // Center the modal on first open.
      if (!this.initialPositionSet) {
        this.$nextTick(() => {
          const modalElement = this.$refs.modalCard as HTMLElement;
          if (modalElement) {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const modalWidth = modalElement.offsetWidth;
            const modalHeight = modalElement.offsetHeight;
            this.modalPosX = Math.max(0, (viewportWidth - modalWidth) / 2);
            this.modalPosY = Math.max(0, (viewportHeight - modalHeight) / 2);
            this.initialPositionSet = true;
          }
        });
      }
    } else {
      // When the modal is closed (e.g., by parent after save, or after this component emits 'close'),
      // reset the centering flag and notify Vuex.
      this.initialPositionSet = false;
      this.notifyModalClosed();
    }
  }

  @Watch('isMinimized')
  public onIsMinimizedChange(
    isMinimized: boolean,
    oldIsMinimized: boolean,
  ): void {
    if (isMinimized && this.isModalActive) {
      document.addEventListener(
        'click',
        // MODIFICATION START: Use the new wrapper method
        this.handleDocumentClickForMinimizedWrapper,
        // MODIFICATION END
        true,
      ); // Use capture phase
    } else if (!isMinimized && oldIsMinimized) {
      document.removeEventListener(
        'click',
        // MODIFICATION START: Use the new wrapper method
        this.handleDocumentClickForMinimizedWrapper,
        // MODIFICATION END
        true,
      );
    }
  }

  /**
   * Handles the mousedown event on the modal header.
   * Initiates the dragging process.
   * @param e - The MouseEvent object.
   */
  public handleMouseDown(e: MouseEvent): void {
    // Only allow dragging if the draggable prop is true
    if (!this.draggable) {
      return;
    }

    const modalElement = this.$refs.modalCard as HTMLElement;
    if (!modalElement) return;

    this.isDragging = true;

    // Record starting mouse position
    this.startMouseX = e.clientX;
    this.startMouseY = e.clientY;

    // Record starting modal position
    this.startModalX = this.modalPosX;
    this.startModalY = this.modalPosY;

    // Add listeners to the window to track mouse movement and release
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);

    // Prevent text selection during drag
    document.body.style.userSelect = 'none';
  }

  /**
   * Handles the mousemove event on the window *during* a drag.
   * Calculates and updates the modal's position based on mouse movement.
   * @param e - The MouseEvent object.
   */
  public handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;

    // Calculate mouse movement delta
    const deltaX = e.clientX - this.startMouseX;
    const deltaY = e.clientY - this.startMouseY;

    // Calculate new modal position.
    // The modal will just follow the mouse.
    this.modalPosX = this.startModalX + deltaX;
    this.modalPosY = this.startModalY + deltaY;
  }

  /**
   * Handles the mouseup event on the window.
   * Finalizes the dragging process.
   */
  public handleMouseUp(): void {
    if (!this.isDragging) return;

    this.isDragging = false;

    // Remove listeners from the window
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);

    // Optional: Re-enable text selection
    document.body.style.userSelect = '';
  }

  public async handleAttemptClose(
    isFromMinimized: boolean = false,
  ): Promise<void> {
    if (!this.isDirty) {
      this.$emit('close');
      return;
    }

    // Prevent dialog from showing if another one is already active.
    if (this.$store.state.unsavedChangesGuard.isUnsavedChangesDialogActive) {
      return;
    }

    const userConfirmed = await showUnsavedChangesDialog();
    if (userConfirmed) {
      // User confirmed discarding changes. Emit 'close' to the parent.
      // The parent will set isModalActive=false, which the watcher will catch
      // to call notifyModalClosed().
      this.$emit('close');
      this.$buefy.toast.open({
        message: 'Changes discarded.',
        duration: 8000,
      });
    } else {
      // User selected "Keep Editing".
      await this.notifyKeepEditingSelected();
      // If the modal was minimized, emit 'restore' so the parent can expand it.
      if (isFromMinimized || this.isMinimized) {
        this.$emit('restore');
      }
    }
  }

  private async internalHandleDocumentClickForMinimized(
    event: MouseEvent,
  ): Promise<void> {
    if (this.$store.state.unsavedChangesGuard.isUnsavedChangesDialogActive) {
      return;
    }

    if (!this.isMinimized || !this.isModalActive) {
      document.removeEventListener(
        'click',
        this.handleDocumentClickForMinimizedWrapper,
        true,
      );
      return;
    }

    const targetElement = event.target as HTMLElement;

    // Do nothing if the click is on the "Resume Edit" overlay.
    if (targetElement.closest('.shared-list-item-edit-overlay')) {
      return;
    }

    const minimisedCardComponent = this.$refs.minimisedCardRef as Vue;
    const minimisedCardElement = minimisedCardComponent.$el;

    // If the click is outside the minimized card itself...
    if (minimisedCardElement && !minimisedCardElement.contains(targetElement)) {
      event.preventDefault();
      event.stopPropagation();

      // Call the common close attempt handler.
      await this.handleAttemptClose(true);
    }
  }

  private handleDocumentClickForMinimizedWrapper(event: MouseEvent): void {
    this.internalHandleDocumentClickForMinimized(event);
  }

  // --- Lifecycle Hook for Cleanup ---
  public beforeDestroy(): void {
    // Ensure listeners are removed if the component is destroyed mid-drag
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    // Reset body style if necessary
    if (this.isDragging) {
      document.body.style.userSelect = '';
    }

    if (this.isMinimized && this.isModalActive) {
      document.removeEventListener(
        'click',
        this.handleDocumentClickForMinimizedWrapper,
        true,
      );

      this.notifyModalClosed();
    }
  }
}
</script>

<style lang="scss" module>
.modalBackground {
  // Positioning to cover viewport
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  // Layout: Center the modal card by default (drag overrides position)
  display: flex;
  justify-content: center;
  align-items: center;

  // Ensure it's above other page content but potentially below other modals if needed
  z-index: 40;
  pointer-events: auto; // Allow clicks to pass through to elements below
}

.displayModal {
  display: flex;
  border: 1px solid rgba($black-1, 0.186);
  border-radius: 5px;
  box-shadow: 0 0 1px 0 rgba($black-1, 0.4), 0 16px 32px rgba($black-1, 0.2);
}

.hideModal {
  display: none;
}

.modalCardHead {
  background-color: $white;
}

// Need to use custom styling for modal card title
// since the available modal-card-title scss class width is too large
// to accomodate the action buttons
.modalCardTitle {
  width: 90%;
  color: $primary;
  font-size: $size-3;
  font-weight: $weight-bold;
  line-height: 22px;
  text-align: left;
}

.headerButton {
  // Reset default button styles
  border: none;
  background: none;
  padding: 0;

  // Appearance
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: $dark;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease-in-out;

  // Ensure icons have a reasonable default size
  i {
    font-size: $size-3;
    line-height: 1;
  }

  // Hover effect
  &:hover {
    background-color: $grey-light-5;
  }

  // Remove outline when focused
  &:focus {
    outline: none;
  }
}

.modalCardFoot {
  background-color: white;
  display: flex;
  justify-content: flex-end;
}
</style>
